import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  type ExperimentAssignment,
  formatRevenue,
  isPaidVariant,
} from "../experiments/experimentClient";
import { eligibilityKind } from "./monetizationModel";

const ELIGIBILITY_BADGE = {
  verified: { label: "Verified", variant: "secondary" },
  policy: { label: "Policy", variant: "outline" },
  mismatch: { label: "Mismatch", variant: "destructive" },
} as const;

export function MonetizationContactAudit({
  assignments,
}: {
  assignments: ExperimentAssignment[];
}) {
  const treatment = assignments.filter((assignment) =>
    isPaidVariant(assignment.variant),
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle>Contact conversion audit</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Contact</TableHead>
              <TableHead>Acquisition</TableHead>
              <TableHead>Eligibility</TableHead>
              <TableHead>Paywall</TableHead>
              <TableHead>Started</TableHead>
              <TableHead>Rated</TableHead>
              <TableHead>Offer sent</TableHead>
              <TableHead>Selected</TableHead>
              <TableHead>Checkout</TableHead>
              <TableHead>Paid</TableHead>
              <TableHead>Orders</TableHead>
              <TableHead>Net revenue</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {treatment.map((assignment) => (
              <TableRow key={assignment.assignment_id}>
                <TableCell>
                  <Link
                    className="font-medium underline-offset-4 hover:underline"
                    to={`/contacts/${encodeURIComponent(assignment.user_id)}/show`}
                  >
                    {assignment.user_id}
                  </Link>
                </TableCell>
                <TableCell>
                  <p className="capitalize">
                    {assignment.acquisition_surface ?? "Unknown"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {[
                      assignment.acquisition_group,
                      assignment.acquisition_platform,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "No evidence"}
                  </p>
                </TableCell>
                <TableCell>
                  <EligibilityStatus assignment={assignment} />
                </TableCell>
                <Status value={assignment.last_paywall_at} />
                <Status value={assignment.last_echo_started_at} />
                <Status value={assignment.last_rating_at} />
                <Status value={assignment.last_offer_prompted_at} />
                <Status value={assignment.last_offer_selected_at} />
                <Status value={assignment.last_checkout_created_at} />
                <Status value={assignment.last_payment_at} />
                <TableCell className="tabular-nums">
                  {assignment.metrics?.paid_orders ?? 0}
                </TableCell>
                <TableCell className="tabular-nums">
                  {assignment.metrics?.paid_orders
                    ? formatRevenue(
                        assignment.metrics.net_revenue_minor,
                        assignment.metrics.currency,
                      )
                    : "—"}
                </TableCell>
              </TableRow>
            ))}
            {!treatment.length ? (
              <TableRow>
                <TableCell
                  colSpan={12}
                  className="text-center text-muted-foreground"
                >
                  No treatment contacts yet.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function EligibilityStatus({
  assignment,
}: {
  assignment: ExperimentAssignment;
}) {
  const badge = ELIGIBILITY_BADGE[eligibilityKind(assignment)];
  return (
    <div className="space-y-1">
      <Badge variant={badge.variant}>{badge.label}</Badge>
      {assignment.eligibility_reason ? (
        <p className="max-w-48 text-xs text-muted-foreground">
          {assignment.eligibility_reason}
        </p>
      ) : null}
    </div>
  );
}

function Status({ value }: { value?: string }) {
  return (
    <TableCell>
      {value ? <Badge variant="secondary">Yes</Badge> : "—"}
    </TableCell>
  );
}
